import { useCallback, useState } from "react";

import { makeGetApiCall } from "../../shared/makeApiCall";
import type {
  DashboardActivity,
  DashboardSummary,
} from "../../views/dashboard/dashboardTypes";

type ApiCallResult = {
  success: boolean;
  data?: unknown;
  errorStatus?: number;
};

function extractErrorMessage(data: unknown): string {
  if (!data || typeof data !== "object")
    return "We could not load the dashboard.";
  const payload = data as {
    message?: string;
    errors?: Record<string, string[] | string>;
  };
  if (typeof payload.message === "string") return payload.message;
  if (payload.errors && typeof payload.errors === "object") {
    const first = Object.values(payload.errors).flat()[0];
    if (typeof first === "string") return first;
  }
  return "We could not load the dashboard.";
}

export function useDashboardViewModel(logout?: () => void) {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [activity, setActivity] = useState<DashboardActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const refresh = useCallback(
    async (from: string, to: string) => {
      setLoading(true);
      setFetchError(null);

      const qs = new URLSearchParams({ from, to }).toString();

      const [summaryResult, activityResult] = (await Promise.all([
        makeGetApiCall(`operations-dashboard/summary?${qs}`),
        makeGetApiCall(`operations-dashboard/activity?${qs}`),
      ])) as [ApiCallResult, ApiCallResult];

      const errorParts: string[] = [];

      if (summaryResult.success) {
        setSummary(summaryResult.data as DashboardSummary);
      } else {
        setSummary(null);
        if (summaryResult.errorStatus === 401) {
          logout?.();
        }
        errorParts.push(extractErrorMessage(summaryResult.data));
      }

      if (activityResult.success) {
        setActivity(activityResult.data as DashboardActivity);
      } else {
        setActivity(null);
        if (activityResult.errorStatus === 401) {
          logout?.();
        }
        errorParts.push(extractErrorMessage(activityResult.data));
      }

      if (errorParts.length) {
        setFetchError(Array.from(new Set(errorParts)).join(" "));
      }

      setLoading(false);
    },
    [logout],
  );

  return {
    summary,
    activity,
    loading,
    fetchError,
    refresh,
  };
}
